// SPDX-License-Identifier: GPL-3.0-or-later

import './ui/styles.css';
import './ui/theme'; // applies persisted/system theme on import

import { isAiBusy, onAiBusyChange } from './ai/client';
import { getExtensionState, subscribeExtension } from './ext/detect';
import { readProfile } from './fs/profile';
import { getRootState, pickRoot, requestRootPermission, type RootState } from './fs/root';
import { ingestHandoff } from './handoff';
import type { JourneyInputs } from './onboarding/journey';
import { autoScreen } from './screen';
import { onRouteChange, parseHash, type Route } from './router';
import { getManifest, loadManifest, subscribe } from './store/applications';
import { renderDashboard, resetDashboardFilter } from './ui/Dashboard';
import { el, mount } from './ui/dom';
import { renderJobDetail } from './ui/JobDetail';
import { renderOnboarding } from './ui/Onboarding';
import { renderPrivacy } from './ui/Privacy';
import { renderSettings } from './ui/Settings';
import { renderShell } from './ui/Shell';
import { renderSmoke } from './ui/Smoke';
import { autoReconnectWallet, getWalletState, subscribeWallet } from './wallet';

const appRoot = document.getElementById('app')!;

let rootHandle: FileSystemDirectoryHandle | null = null;
let previousRouteName: Route['name'] | null = null;

async function refreshRoot(): Promise<RootState> {
  const state = await getRootState();
  rootHandle = state.permission === 'granted' ? state.handle : null;
  return state;
}

async function render(): Promise<void> {
  const route = parseHash(location.hash);
  if (route.name === 'dashboard' && previousRouteName === 'job') {
    resetDashboardFilter();
  }
  previousRouteName = route.name;
  await routeTo(route);
}

async function routeTo(route: Route): Promise<void> {
  switch (route.name) {
    case 'smoke': {
      mount(appRoot, renderShell(renderSmoke(), { showSettings: false, rootName: rootHandle?.name }));
      return;
    }
    case 'privacy': {
      mount(appRoot, renderShell(renderPrivacy(), { showSettings: false, rootName: rootHandle?.name }));
      return;
    }
    case 'handoff': {
      // Snapshot params so subscribe-driven re-renders don't re-process them
      // mid-flight (addApplication dedupes by jobUrl, so even if they did, the
      // second pass is a no-op).
      const params = { ...route.params };

      const state = await refreshRoot();
      if (!state.handle) {
        const body = el(
          'section',
          { style: 'max-width: 38rem; display: flex; flex-direction: column; gap: 1rem;' },
          el('h2', { style: 'font-family: var(--font-serif); font-style: italic; font-size: 2rem;' }, 'Set up Applify first'),
          el(
            'p',
            { style: 'color: var(--text-secondary);' },
            'A job was sent to Applify, but no folder is selected yet. Finish onboarding, then click the extension again to save this job.',
          ),
          el(
            'div',
            {},
            el(
              'button',
              {
                class: 'btn primary',
                onclick: () => {
                  history.replaceState(null, '', location.pathname + location.search + '#/');
                  void render();
                },
              },
              'Go to onboarding',
            ),
          ),
        );
        mount(appRoot, renderShell(body, { showSettings: false }));
        return;
      }

      const handle = state.handle;

      const finishIngest = async (): Promise<void> => {
        if (!getManifest()) await loadManifest(handle);
        const outcome = await ingestHandoff(params);
        // Strip the payload from the URL before re-rendering, regardless of
        // outcome — reloading shouldn't re-ingest a stale payload.
        history.replaceState(null, '', location.pathname + location.search + '#/');
        if (!outcome.ok) {
          const err = el(
            'section',
            { style: 'max-width: 38rem; display: flex; flex-direction: column; gap: 1rem;' },
            el('h2', { style: 'font-family: var(--font-serif); font-style: italic; font-size: 2rem;' }, 'Handoff failed'),
            el('p', { style: 'color: var(--status-rejected);' }, outcome.message),
            el(
              'div',
              {},
              el('button', { class: 'btn', onclick: () => void render() }, 'Back to dashboard'),
            ),
          );
          mount(appRoot, renderShell(err, { showSettings: false, rootName: handle.name }));
          return;
        }

        // Auto-screen against preferences (best-effort, ignored on failure).
        if (outcome.result.created) {
          const loading = el(
            'section',
            { style: 'max-width: 38rem; display: flex; flex-direction: column; gap: 0.75rem;' },
            el(
              'h2',
              { style: 'font-family: var(--font-serif); font-style: italic; font-size: 2rem;' },
              `Saved · ${outcome.result.application.jobTitle}`,
            ),
            el('p', { style: 'color: var(--text-secondary);' }, 'Auto-screening against your preferences…'),
          );
          mount(appRoot, renderShell(loading, { showSettings: false, rootName: handle.name }));
          const screen = await autoScreen(outcome.result.application);
          if (screen.ran && screen.result && !screen.result.match) {
            // Status update has already persisted; the dashboard render
            // below will reflect Filtered.
            // Surface the reason in a banner before redirecting.
            const banner = el(
              'section',
              { style: 'max-width: 38rem; display: flex; flex-direction: column; gap: 0.75rem;' },
              el(
                'h2',
                { style: 'font-family: var(--font-serif); font-style: italic; font-size: 2rem;' },
                'Filtered',
              ),
              el(
                'p',
                { style: 'color: var(--text-secondary);' },
                screen.result.reason,
              ),
              el(
                'div',
                { style: 'display: flex; gap: 0.5rem;' },
                el('button', { class: 'btn', onclick: () => void render() }, 'OK, dashboard'),
                el(
                  'a',
                  { class: 'btn', href: `#/job?id=${outcome.result.application.id}` },
                  'Open job',
                ),
              ),
            );
            mount(appRoot, renderShell(banner, { showSettings: false, rootName: handle.name }));
            return;
          }
        }

        void render();
      };

      if (state.permission !== 'granted') {
        // We have the handle in IDB but the new tab needs a one-click resume
        // to (re)grant the readwrite permission. Permission requests must come
        // from a user gesture.
        const errMsg = el('p', { style: 'color: var(--status-rejected); min-height: 1.2em;' });
        const button = el(
          'button',
          {
            class: 'btn primary',
            onclick: async () => {
              try {
                const perm = await requestRootPermission(handle);
                if (perm !== 'granted') {
                  errMsg.textContent = 'Permission was not granted.';
                  return;
                }
                rootHandle = handle;
                await finishIngest();
              } catch (err) {
                errMsg.textContent = err instanceof Error ? err.message : String(err);
              }
            },
          },
          `Save to "${handle.name}"`,
        );
        const body = el(
          'section',
          { style: 'max-width: 38rem; display: flex; flex-direction: column; gap: 1rem;' },
          el('h2', { style: 'font-family: var(--font-serif); font-style: italic; font-size: 2rem;' }, 'One click to save'),
          el(
            'p',
            { style: 'color: var(--text-secondary);' },
            `A job is waiting to be saved to "${handle.name}". Browsers re-confirm folder access on each new tab — click to allow.`,
          ),
          el('div', {}, button),
          errMsg,
        );
        mount(appRoot, renderShell(body, { showSettings: false, rootName: handle.name }));
        return;
      }

      await finishIngest();
      return;
    }
    case 'onboarding':
    case 'dashboard':
    case 'settings':
    case 'job': {
      const state = await refreshRoot();
      if (!state.handle || state.permission !== 'granted') {
        const body = renderOnboarding({
          onReady: (handle) => {
            rootHandle = handle;
            location.hash = '#/';
            void render();
          },
        });
        mount(appRoot, renderShell(body, { showSettings: false }));
        return;
      }

      if (!getManifest()) {
        await loadManifest(state.handle);
      }

      if (route.name === 'settings') {
        const body = renderSettings({
          root: state.handle,
          focusSection: route.params.focus,
          onReselect: async () => {
            try {
              const next = await pickRoot();
              rootHandle = next;
              await loadManifest(next);
              location.hash = '#/';
              void render();
            } catch (err) {
              if (err instanceof DOMException && err.name === 'AbortError') return;
              alert(`Re-select failed: ${err instanceof Error ? err.message : String(err)}`);
            }
          },
        });
        mount(appRoot, renderShell(body, { showSettings: false, rootName: state.handle.name }));
        return;
      }

      const manifest = getManifest()!;

      if (route.name === 'job') {
        const id = route.params['id'];
        const app = id ? manifest.applications.find((a) => a.id === id) : undefined;
        if (!app) {
          const body = el(
            'section',
            { style: 'max-width: 38rem; display: flex; flex-direction: column; gap: 1rem;' },
            el('h2', { style: 'font-family: var(--font-serif); font-style: italic; font-size: 2rem;' }, 'Job not found'),
            el(
              'p',
              { style: 'color: var(--text-secondary);' },
              'No application with that id. It may have been removed.',
            ),
            el('div', {}, el('a', { class: 'btn', href: '#/' }, '← Dashboard')),
          );
          mount(appRoot, renderShell(body, { showSettings: true, rootName: state.handle.name }));
          return;
        }
        const body = renderJobDetail({ app, manifest, root: state.handle });
        mount(appRoot, renderShell(body, { showSettings: true, rootName: state.handle.name }));
        return;
      }

      let hasProfile = false;
      try {
        const profile = await readProfile(state.handle);
        hasProfile = !!(
          profile &&
          ((profile.rawText && profile.rawText.trim()) ||
            (profile.preferences && profile.preferences.trim()))
        );
      } catch {
        /* corrupt/unreadable profile.json — treat as not set */
      }
      const journey: JourneyInputs = {
        folderPicked: true,
        hasProfile,
        extInstalled: getExtensionState().installed,
        hasJobs: manifest.applications.length > 0,
        walletConnected: getWalletState().connection !== null,
        hasGeneratedDoc: manifest.applications.some(
          (a) => a.generated && (a.generated.cv || a.generated.cover || a.generated.interview),
        ),
      };
      const body = renderDashboard({ manifest, root: state.handle, journey });
      mount(appRoot, renderShell(body, { showSettings: true, rootName: state.handle.name }));
      return;
    }
  }
}

onRouteChange(() => {
  void render();
});

// Re-render the current route whenever the store changes (status update,
// config edit, BroadcastChannel message from another tab).
subscribe(() => {
  void render();
});

// Re-render when the browser extension announces itself (flips onboarding /
// journey from "install the extension" to "installed").
subscribeExtension(() => {
  void render();
});

// Re-render when the wallet connection state changes (header chip + Settings).
//
// Defer renders while an AI call is in flight: the wallet emits a
// disconnect/reconnect pair when it auto-locks mid-call, and a re-render
// would tear down the in-progress JobDetail (wiping "Generating…" and
// detaching the status element the runGenerate closure writes to). Flush
// once the AI call settles.
let lastWalletConnected = false;
let firstWalletPaint = true;
let pendingWalletRender = false;
subscribeWallet((s) => {
  const isConnected = s.connection !== null;
  if (firstWalletPaint) {
    firstWalletPaint = false;
    lastWalletConnected = isConnected;
    return;
  }
  if (isConnected !== lastWalletConnected) {
    lastWalletConnected = isConnected;
    if (isAiBusy()) {
      pendingWalletRender = true;
      return;
    }
    void render();
  }
});
onAiBusyChange(() => {
  if (!isAiBusy() && pendingWalletRender) {
    pendingWalletRender = false;
    void render();
  }
});

void render();

// Silent reconnect after first paint so the wallet chip flips from "Connect"
// to "Wallet · …" without the user clicking when they were connected before.
void autoReconnectWallet();
